'use client';

import { useMemo } from 'react';
import { BiSend } from 'react-icons/bi';
import CompactRepairItem from './CompactRepairItem';
import QwikFixLogo from './QwikFixLogo';
import QwikFixToggle from './QwikFixToggle';
import { QWIKFIX_BRAND } from './qwikfixBrand';
import { QWIKFIX_BENEFITS, QWIKFIX_PRICING } from './mockData';
import { QWIKFIX_WEBSITE_URL } from './qwikfixBrand';
import { getQwikFixScopedItems } from './mapRepairDataForQuickFixModal';
import type {
  QuickFixBenefitsLayout,
  QuickFixRepairItem,
} from './types';

function UserIcon() {
  return (
    <svg width='14' height='14' viewBox='0 0 16 16' fill='none' aria-hidden>
      <circle cx='8' cy='5.5' r='2.5' stroke='currentColor' strokeWidth='1.5' />
      <path
        d='M3 13.5c0-2.8 2.2-5 5-5s5 2.2 5 5'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
      />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg width='14' height='14' viewBox='0 0 16 16' fill='none' aria-hidden>
      <rect
        x='2.5'
        y='1.5'
        width='11'
        height='13'
        rx='1.5'
        stroke='currentColor'
        strokeWidth='1.5'
      />
      <path
        d='M5 5h6M5 8h6M5 11h4'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
      />
    </svg>
  );
}

export default function QwikFixCard({
  allItems,
  benefitsLayout = 'grid',
  sent,
  isSending = false,
  onSend,
  shareWithClient,
  onShareWithClientChange,
  quoteWholeReport,
  onQuoteWholeReportChange,
  companyName,
  reportDeficiencyCount = 0,
  sendDisabled: sendDisabledProp = false,
}: {
  allItems: QuickFixRepairItem[];
  benefitsLayout?: QuickFixBenefitsLayout;
  sent: boolean;
  isSending?: boolean;
  onSend: () => void;
  shareWithClient: boolean;
  onShareWithClientChange: (value: boolean) => void;
  quoteWholeReport: boolean;
  onQuoteWholeReportChange: (value: boolean) => void;
  companyName?: string;
  reportDeficiencyCount?: number;
  sendDisabled?: boolean;
}) {
  const totalCount = allItems.length;
  const uncategorizedCount = useMemo(
    () => allItems.filter((it) => !it.isCategorizedByRepairCompany).length,
    [allItems]
  );
  const items = useMemo(
    () => getQwikFixScopedItems(allItems, quoteWholeReport),
    [allItems, quoteWholeReport]
  );
  const allItemsMatchedToSpecialty =
    uncategorizedCount === 0 && !quoteWholeReport;
  const quotingFullReport = quoteWholeReport;
  const reportFindingLabel =
    reportDeficiencyCount === 1 ? 'finding' : 'findings';
  const repairListLabel = totalCount === 1 ? 'item' : 'items';

  const sendDisabled = sendDisabledProp || sent || isSending || items.length === 0;

  return (
    <div
      className='relative isolate mb-4 rounded-xl border border-[#42b9db]/20 text-[#00394d] shadow-[0_4px_20px_-6px_rgba(1,68,90,0.18)]'
      style={{ background: QWIKFIX_BRAND.cardGradientLight }}
    >
      <div
        className='pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-xl opacity-60'
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, rgba(66,185,219,0.08) 0 2px, transparent 2px), radial-gradient(circle at 80% 70%, rgba(42,162,204,0.06) 0 2px, transparent 2px)',
          backgroundSize: '24px 24px',
        }}
        aria-hidden
      />
      <div
        className='pointer-events-none absolute inset-0 z-0 overflow-hidden rounded-xl bg-[radial-gradient(120%_100%_at_100%_0%,rgba(192,232,243,0.55),transparent_50%),radial-gradient(80%_100%_at_0%_100%,rgba(66,185,219,0.12),transparent_55%)]'
        aria-hidden
      />

      <div className='relative z-10 p-4'>
        <div className='mb-3.5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div className='min-w-0 flex-1 space-y-2'>
            <QwikFixLogo
              variant='light'
              className='h-7 w-auto max-w-[min(100%,200px)]'
            />
            <div className='flex flex-wrap items-center gap-2'>
              <span className='inline-flex items-center rounded-full border border-[#42b9db]/35 bg-[#42b9db]/12 px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-[#027B9F]'>
                Recommended · Catch-all
              </span>
              <span className='text-[10px] font-semibold uppercase tracking-wider text-default-400'>
                Powered by TheQwikFix®
              </span>
            </div>
          </div>
          <button
            type='button'
            onClick={onSend}
            disabled={sendDisabled}
            aria-label={
              sent
                ? 'QwikFix quote sent from card'
                : isSending
                  ? 'Sending QwikFix quote from card'
                : items.length === 0
                  ? 'Send to QwikFix from card (no items — enable whole report)'
                : `Send ${items.length} items to QwikFix from card`
            }
            className={`inline-flex w-full shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 py-2 text-xs font-bold transition sm:w-auto ${
              sendDisabled
                ? 'cursor-not-allowed bg-default-100 text-default-400 shadow-none'
                : 'cursor-pointer text-white shadow-[0_4px_14px_-2px_rgba(2,161,207,0.35)] hover:-translate-y-px hover:shadow-[0_6px_16px_-2px_rgba(2,161,207,0.45)]'
            }`}
            style={
              sendDisabled ? undefined : { background: QWIKFIX_BRAND.buttonGradient }
            }
          >
            {sent ? (
              <>
                <span>✓</span>
                <span>Sent to QwikFix</span>
              </>
            ) : isSending ? (
              <>
                <span>...</span>
                <span>Sending to QwikFix</span>
              </>
            ) : (
              <>
                <BiSend size={14} />
                <span>
                  Send {items.length} {items.length === 1 ? 'item' : 'items'}
                </span>
              </>
            )}
          </button>
        </div>

        <div
          className={
            benefitsLayout === 'strip'
              ? 'mb-3 space-y-1 rounded-lg border border-[#42b9db]/15 bg-white/70 px-2.5 py-2'
              : 'mb-3 grid grid-cols-1 gap-2 rounded-lg border border-[#42b9db]/15 bg-white/70 px-2.5 py-2 sm:grid-cols-2 sm:gap-3'
          }
        >
          {QWIKFIX_BENEFITS.map((b) => (
            <div key={b.title} className='flex min-w-0 items-start gap-1.5'>
              <span className='grid h-5 w-5 shrink-0 place-items-center rounded bg-[#C0E8F3] text-[10px] text-[#027B9F]'>
                {b.icon}
              </span>
              <div className='min-w-0'>
                <p className='text-[10px] font-bold uppercase leading-tight tracking-wide text-[#027B9F]'>
                  {b.title}
                </p>
                <p className='text-[10px] leading-snug text-default-500'>{b.sub}</p>
              </div>
            </div>
          ))}
        </div>

        <div className='mb-3 rounded-lg border border-[#42b9db]/15 bg-white/90 px-3 pb-2 pt-2.5'>
          {allItemsMatchedToSpecialty ? (
            <p className='text-xs leading-snug text-default-600'>
              All items matched to specialty contractors. You can only quote the
              whole report.
            </p>
          ) : quotingFullReport ? (
            <>
              <div className='mb-2 flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-default-500'>
                <span>Will quote · full inspection report</span>
                <span className='inline-flex items-center rounded-full border border-[#42b9db]/30 bg-[#C0E8F3]/60 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-[#027B9F]'>
                  Whole report
                </span>
              </div>
              <div className='rounded-lg border border-[#42b9db]/25 bg-[#C0E8F3]/35 px-3 py-2.5'>
                <p className='text-xs font-semibold leading-snug text-[#00394d]'>
                  The full inspection report will be sent to QwikFix for quoting.
                </p>
                <p className='mt-1 text-[11px] leading-snug text-default-600'>
                  {reportDeficiencyCount > 0 ? (
                    <>
                      QwikFix will review all{' '}
                      <span className='font-semibold text-[#027B9F]'>
                        {reportDeficiencyCount} report {reportFindingLabel}
                      </span>
                      , not only the {totalCount} {repairListLabel} on your repair
                      list.
                    </>
                  ) : (
                    <>
                      QwikFix will review findings across the full report, not only
                      the {totalCount} {repairListLabel} on your repair list.
                    </>
                  )}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className='mb-1.5 flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-default-500'>
                <span>
                  Will quote · {items.length}{' '}
                  {items.length === 1 ? 'item' : 'items'}
                </span>
              </div>
              <div className='max-h-40 overflow-y-auto'>
                <div className='grid grid-cols-1 gap-x-4 gap-y-0 sm:grid-cols-2'>
                  {items.map((it) => (
                    <CompactRepairItem key={it.id} item={it} />
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
          <QwikFixToggle
            icon={<UserIcon />}
            label='Share with client'
            sub='CC the buyer on quote updates & messaging'
            value={shareWithClient}
            onChange={onShareWithClientChange}
            onLight
          />
          <QwikFixToggle
            icon={<ReportIcon />}
            label='Quote whole report'
            sub={
              quoteWholeReport
                ? reportDeficiencyCount > 0
                  ? `Full report · ${reportDeficiencyCount} report ${reportFindingLabel}`
                  : 'Full inspection report · all findings'
                : `Uncategorized only · ${uncategorizedCount} of ${totalCount} items`
            }
            value={quoteWholeReport}
            onChange={onQuoteWholeReportChange}
            onLight
          />
        </div>

        <div className='mt-3 border-t border-[#42b9db]/15 pt-3'>
          <div className='flex flex-wrap items-end justify-center gap-x-2 gap-y-1'>
            <span className='font-mono text-4xl font-black leading-none tracking-tight text-default-300 line-through'>
              ${QWIKFIX_PRICING.listPrice}
            </span>
            <span className='font-mono text-4xl font-black leading-none tracking-tight text-[#027B9F]'>
              ${QWIKFIX_PRICING.promoPrice}
            </span>
            <span className='pb-1 font-mono text-sm font-bold uppercase tracking-wide text-[#00394d]'>
              {QWIKFIX_PRICING.label}
            </span>
          </div>
          <p className='mt-2 text-center text-[10px] leading-snug text-default-500'>
            {companyName?.trim()
              ? `Free trial for a limited time for ${companyName.trim()} customers. `
              : 'Free trial for a limited time. '}
            After our promotional period, enjoy discounted pricing through us. QwikFix
            is a partner
            that specializes in home inspection report quotes.{' '}
            <a
              href={QWIKFIX_WEBSITE_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='font-medium text-[#027B9F] hover:underline'
            >
              Learn more about them here
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
}
